import { Link } from 'react-router-dom';
import './Legal.css';

export default function PrivacyPolicy() {
  return (
    <div className="privacy-policy-page">
      <div className="page-hero">
        <div className="container">
          <div className="badge">Legal</div>
          <h1>Privacy Policy</h1>
          <p>How Aarambh collects, uses and protects the information you share with us.</p>
        </div>
      </div>

      <section className="legal-section">
        <div className="container legal-card">
          <p className="legal-updated">Last updated: 7 August 2026</p>

          <p className="legal-intro">
            Aarambh ("we", "us", "our") operates this platform to connect Ganpati Mahotsav Mandals across
            Valsad district, Gujarat, enable mandal registration, facilitate donations and coordinate social
            activities. This Privacy Policy explains what information we collect, how we use it, who we share
            it with, and the choices you have. By using this website or any of our portals, you agree to the
            practices described here.
          </p>

          <div className="legal-block">
            <h2>1. Information We Collect</h2>
            <h3>a. Mandal Registration Information</h3>
            <p>
              When a mandal registers on Aarambh, we collect details such as the mandal name, area/taluka and
              address, name and contact details of the secretary/contact person, phone number, WhatsApp number,
              email address, and any logo, cover photo or gallery images uploaded for the mandal's profile. A
              password is also created to access the mandal portal.
            </p>
            <h3>b. Donation & Donor Information</h3>
            <p>
              When you make a donation, we collect the donor's name, email, phone number, donation amount and
              selected cause, unless the donation is made anonymously. Payment details (card, UPI, net banking
              information) are collected and processed directly by our payment partner, Razorpay — we do not
              store your card or bank details on our servers.
            </p>
            <h3>c. Contact Form Submissions</h3>
            <p>
              When you write to us through the Contact Us form, we collect your name, email address, phone
              number, subject and message so that we can respond to your query.
            </p>
            <h3>d. Account & Login Information</h3>
            <p>
              Mandal representatives and administrators who log in to the mandal or admin portals are issued
              secure session cookies/tokens to keep them authenticated. We do not store passwords in plain text.
            </p>
            <h3>e. Automatically Collected Information</h3>
            <p>
              Like most websites, our servers may automatically log technical information such as IP address,
              browser type, device information and access timestamps for security and troubleshooting purposes.
            </p>
          </div>

          <div className="legal-block">
            <h2>2. How We Use Your Information</h2>
            <ul>
              <li>To review, approve and list mandal registrations on our public directory.</li>
              <li>To process donations, issue acknowledgements/receipts and maintain transparent records.</li>
              <li>To respond to enquiries submitted via the Contact Us form.</li>
              <li>To send transactional communications — registration confirmations, password setup emails, donation receipts and contact acknowledgements.</li>
              <li>To coordinate and publicise mandal events and social activities.</li>
              <li>To maintain the security, integrity and proper functioning of our platform.</li>
              <li>To comply with applicable legal and regulatory requirements.</li>
            </ul>
            <p>We do not sell, rent or trade your personal information to third parties for marketing purposes.</p>
          </div>

          <div className="legal-block">
            <h2>3. Payment Processing</h2>
            <p>
              All donations are processed through Razorpay, a PCI-DSS compliant, RBI-regulated payment gateway.
              When you make a payment, you are subject to Razorpay's own privacy policy and terms in addition to
              this one. Aarambh only receives confirmation of successful payment, the transaction reference and
              the amount — we never see or store your full card, UPI or bank credentials.
            </p>
          </div>

          <div className="legal-block">
            <h2>4. Email Communications</h2>
            <p>
              We use Brevo, a third-party transactional email service, to send registration confirmations,
              password-setup links, donation acknowledgements and responses to contact form submissions. Your
              name and email address are shared with Brevo solely for the purpose of delivering these emails.
            </p>
          </div>

          <div className="legal-block">
            <h2>5. Sharing of Information</h2>
            <p>We share personal information only in the following limited circumstances:</p>
            <ul>
              <li>With trusted service providers who process data on our behalf (Razorpay for payments, Brevo for email, and our hosting/infrastructure provider).</li>
              <li>Where a donor has not opted for anonymity, the donor's name may be displayed publicly in donor acknowledgements as part of our transparency practices.</li>
              <li>If required to comply with a legal obligation, court order or governmental request.</li>
              <li>To protect the rights, property or safety of Aarambh, our users or the public.</li>
            </ul>
          </div>

          <div className="legal-block">
            <h2>6. Cookies & Session Storage</h2>
            <p>
              We use essential cookies and browser storage to keep you logged in to the mandal and admin portals
              and to remember basic site preferences. These are functional cookies necessary for the platform to
              work and are not used for third-party advertising or cross-site tracking.
            </p>
          </div>

          <div className="legal-block">
            <h2>7. Data Retention</h2>
            <p>
              We retain registration, donation and communication records for as long as necessary to fulfil the
              purposes described in this policy, to maintain accurate financial and donation records, and to
              comply with applicable legal, accounting or regulatory requirements.
            </p>
          </div>

          <div className="legal-block">
            <h2>8. Data Security</h2>
            <p>
              We take reasonable technical and organisational measures — including encrypted connections,
              hashed passwords and access-controlled admin systems — to protect your information from
              unauthorised access, alteration, disclosure or destruction. However, no method of transmission or
              storage over the internet is 100% secure, and we cannot guarantee absolute security.
            </p>
          </div>

          <div className="legal-block">
            <h2>9. Your Rights</h2>
            <p>
              You may request access to, correction of, or deletion of your personal information held by us,
              subject to our legitimate need to retain certain records (such as donation records for financial
              compliance). To exercise these rights, please contact us using the details below.
            </p>
          </div>

          <div className="legal-block">
            <h2>10. Children's Privacy</h2>
            <p>
              Aarambh is intended for use by mandal representatives, donors and members of the public who are
              at least 18 years of age. We do not knowingly collect personal information from children.
            </p>
          </div>

          <div className="legal-block">
            <h2>11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices or for
              legal reasons. The "Last updated" date at the top of this page will indicate when the policy was
              last revised. Continued use of the platform after changes are posted constitutes acceptance of the
              revised policy.
            </p>
          </div>

          <div className="legal-block">
            <h2>12. Contact Us</h2>
            <p>If you have questions or concerns about this Privacy Policy or how your data is handled, please reach out to us:</p>
            <div className="legal-contact-box">
              <p><strong>Aarambh</strong></p>
              <p>Valsad, Gujarat, India</p>
              <p>Or use our <Link to="/contact">Contact Us</Link> page.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
